import { useCallback, useEffect, useRef, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { ORPCError } from '@orpc/client'
import type { OrpcClient } from '@/orpc/client'
import { useClient } from './useClient'

export type RecordLocatorEnrichment = NonNullable<
  Awaited<ReturnType<OrpcClient['runRecordLocatorEnrichment']>>
>
export type EnrichedRow = RecordLocatorEnrichment['providers'][number]

const POLL_INTERVAL_MS = 4000
const POLL_TIMEOUT_MS = 5 * 60_000

/**
 * Returns true for permanent auth errors (401/403) that should stop retrying
 * immediately. Everything else (503, network, timeout, unknown) is transient.
 */
function isPermanentError(error: unknown): boolean {
  if (error instanceof ORPCError) {
    return error.status === 401 || error.status === 403
  }
  // Fallback: check code/message strings for cases where instanceof doesn't
  // work across bundle boundaries (oRPC's own warning in the type definition).
  if (error != null && typeof error === 'object') {
    const e = error as Record<string, unknown>
    if (e['status'] === 401 || e['status'] === 403) return true
    if (e['code'] === 'UNAUTHORIZED' || e['code'] === 'FORBIDDEN') return true
  }
  return false
}

/**
 * Fetches the record-locator enrichment for a patient. The run is executed in
 * the background by the middleware (it returns immediately with a `running`
 * status), so this hook polls the GET endpoint until the status is terminal
 * rather than holding a single long request open.
 */
export function useRecordLocatorEnrichment(patientId: string | undefined, active: boolean) {
  const client = useClient()
  const [data, setData] = useState<RecordLocatorEnrichment | null>(null)
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState<{
    pct: number
    completed: number
    total: number
  } | null>(null)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Whether a get/run cycle has already been kicked off for the current patient,
  // so the auto-load effect fires once per patient (not on every re-render).
  const startedRef = useRef(false)
  // Gates the in-flight poll cycle. Set false on cleanup/patient-change so an
  // awaited request that resolves after unmount can't fire state updates or
  // schedule another poll; re-enabled when a new load starts.
  const aliveRef = useRef(true)
  // One provider refresh per patient. The server reports staleness on every read,
  // and a refresh that can't resolve the new provider leaves it stale — so without
  // this the poll loop would re-request it every few seconds.
  const providerRefreshTriedRef = useRef(false)

  const clearPoll = useCallback(() => {
    aliveRef.current = false
    if (pollRef.current) {
      clearTimeout(pollRef.current)
      pollRef.current = null
    }
    setProgress(null)
  }, [])

  // Reset on patient change so the previous patient's data never shows, and any
  // in-flight poll for the old patient is stopped.
  useEffect(() => {
    clearPoll()
    startedRef.current = false
    providerRefreshTriedRef.current = false
    setData(null)
    setLoading(false)
    setProgress(null)
  }, [patientId, clearPoll])

  const load = useCallback(
    async (forceRun: boolean, deadlineArg?: number) => {
      if (!patientId) return
      clearPoll()
      aliveRef.current = true
      // An explicit Rerun re-derives the attempted set, so allow the provider
      // refresh to be considered again for this patient.
      if (forceRun) providerRefreshTriedRef.current = false
      setLoading(true)
      const deadline = deadlineArg ?? Date.now() + POLL_TIMEOUT_MS

      const fail = (message: string) => {
        setLoading(false)
        showNotification({ color: 'red', title: 'Record locator failed', message })
      }

      // Apply a fetched result; returns whether the cycle is finished, should keep
      // polling, or needs the provider rows refreshed first. Mid-run we still
      // surface any prior data so it isn't blank.
      const apply = (
        result: RecordLocatorEnrichment | null,
      ): 'done' | 'poll' | 'empty' | 'refresh-providers' => {
        if (!result) return 'empty'
        if (result.providers.length || result.locations.length || result.status === 'complete') {
          setData(result)
        }
        if (result.status === 'running' && result.progress) {
          const { completed, total } = result.progress
          const pct = total > 0 ? Math.min(99, Math.round((completed / total) * 100)) : 0
          setProgress({ pct, completed, total })
        }
        // Checked before the terminal branches: a complete-but-stale result should
        // refresh rather than finish. At most once per patient — if the refresh
        // can't clear the staleness (an NPI outage leaves the attempted set alone,
        // so the server keeps reporting stale) a second attempt would spin.
        if (result.providersStale && !providerRefreshTriedRef.current) {
          providerRefreshTriedRef.current = true
          return 'refresh-providers'
        }
        if (result.status === 'complete') {
          const total = result.progress?.total ?? 0
          setProgress({ pct: 100, completed: total, total })
          setLoading(false)
          return 'done'
        }
        if (result.status === 'failed') {
          fail('Enrichment failed. Try Rerun.')
          return 'done'
        }
        return 'poll'
      }

      // Ask the server to re-run the provider lookup, then carry on with whatever
      // it reports. The read endpoint only flags staleness; starting the work is an
      // explicit call, so an incidental read can never kick one off.
      const refreshProviders = async (): Promise<'done' | 'poll' | 'empty'> => {
        try {
          const refreshed = await client.refreshRecordLocatorProviders({ patientId })
          if (!aliveRef.current) return 'done'
          // null means the enrichment vanished between the two calls — nothing to
          // poll for, and treating it as 'empty' would launch a full AI rerun.
          if (!refreshed) return 'done'
          const next = apply(refreshed)
          return next === 'refresh-providers' ? 'poll' : next
        } catch {
          // Couldn't start it; the rows we already have stay on screen.
          return 'done'
        }
      }

      const poll = async () => {
        if (!aliveRef.current) return
        if (Date.now() > deadline) {
          fail('Enrichment is taking longer than expected. Try Rerun.')
          return
        }
        try {
          const result = await client.getRecordLocatorEnrichment({ patientId })
          if (!aliveRef.current) return
          let state: 'done' | 'poll' | 'empty' | 'refresh-providers' = apply(result)
          if (state === 'refresh-providers') state = await refreshProviders()
          if (!aliveRef.current) return
          if (state === 'poll') {
            pollRef.current = setTimeout(() => void poll(), POLL_INTERVAL_MS)
          }
        } catch (error) {
          if (!aliveRef.current) return
          if (isPermanentError(error)) {
            fail(error instanceof Error ? error.message : 'Access denied')
          } else if (Date.now() > deadline) {
            fail('Enrichment is taking longer than expected. Try Rerun.')
          } else {
            // Transient error (503, network, etc.) — keep retrying until deadline.
            pollRef.current = setTimeout(() => void poll(), POLL_INTERVAL_MS)
          }
        }
      }

      try {
        // Look for a cached/in-progress result first (unless forcing a rerun).
        const current = forceRun ? null : await client.getRecordLocatorEnrichment({ patientId })
        if (!aliveRef.current) return
        let state: 'done' | 'poll' | 'empty' | 'refresh-providers' = apply(current)
        if (state === 'refresh-providers') state = await refreshProviders()
        if (!aliveRef.current) return
        if (state === 'done') return
        if (state === 'empty' || forceRun) {
          // Nothing cached (or a forced rerun): kick off the background run.
          const started = await client.runRecordLocatorEnrichment({ patientId })
          if (!aliveRef.current) return
          if (apply(started) === 'done') return
        }
        pollRef.current = setTimeout(() => void poll(), POLL_INTERVAL_MS)
      } catch (error) {
        if (!aliveRef.current) return
        if (isPermanentError(error)) {
          fail(error instanceof Error ? error.message : 'Access denied')
        } else if (Date.now() > deadline) {
          fail('Enrichment is taking longer than expected. Try Rerun.')
        } else {
          // Transient initial error (503, network, etc.) — retry the whole cycle
          // after a poll interval rather than surfacing a scary red toast.
          pollRef.current = setTimeout(() => void load(forceRun, deadline), POLL_INTERVAL_MS)
        }
      }
    },
    [client, patientId, clearPoll],
  )

  // Load when the Enriched view becomes active; stop polling on unmount or when
  // the patient/view changes. On cleanup we also clear `startedRef` so that
  // toggling away (e.g. to the TEFCA view) and back RE-RUNS `load`: a fresh GET
  // re-syncs to an in-flight run (status `running` + live progress), so the
  // progress bar resumes at the current % and the data refreshes on completion
  // instead of being stuck at 0% until a full reload.
  useEffect(() => {
    if (active && !startedRef.current) {
      startedRef.current = true
      void load(false)
    }
    return () => {
      clearPoll()
      startedRef.current = false
    }
  }, [active, patientId, load, clearPoll])

  return { data, loading, progress, refresh: () => void load(true) }
}
